import { useState, useCallback } from 'react';
import Lightbox from '@/components/base/Lightbox';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

const rdImages: LightboxImage[] = [
  {
    src: 'https://rixingroup.cn/wp-content/uploads/2025/07/7edbc80521c9a2598db588a2ddc40f5f-scaled.jpg',
    alt: 'Instalaciones de investigación y desarrollo de Rixin Group',
    caption: 'Centro de I+D Rixin Group',
  },
];

const rdAreas = [
  {
    icon: 'ri-microscope-line',
    title: 'Investigación Científica',
    description:
      'Rixin Group cuenta con un sólido equipo de investigación y reservas técnicas perfectas en el campo de los inhibidores de corrosión. Actualmente dispone de 200 investigadores, 60 de nivel de doctorado (incluyendo 15 PhDs y 50 maestrías). La dirección profesional y la escala de talentos son razonables y equilibradas.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Filosofía de Desarrollo',
    description:
      'Rixin Group posee una plataforma y mecanismo de innovación científica y tecnológica sólidos. Ha logrado una serie de resultados de investigación de propiedad intelectual. Cuenta con un gran número de talentos senior en I+D y diseño en la industria de inhibidores de corrosión, y ha obtenido la identificación de empresa nacional de alta tecnología.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Garantía de Calidad',
    description:
      'La empresa siempre se ha enfocado en la innovación tecnológica. Sus productos continúan innovando y su excelente calidad ha ganado el reconocimiento de los clientes. Los productos principales son inhibidores de corrosión, utilizados durante años en CNPC, Sinopec, CNOOC y Yanchang, con excelentes resultados y reputación. Los productos alcanzan el nivel internacional avanzado.',
  },
];

export default function RixinRD() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback(() => setLightboxIndex(0), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  return (
    <section className="bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-14 md:mb-18">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div
                onClick={openLightbox}
                className="group relative rounded-lg overflow-hidden border border-background-200/60 aspect-[4/3] cursor-pointer select-none"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openLightbox(); }}
              >
                <img
                  src={rdImages[0].src}
                  alt={rdImages[0].alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 select-none"
                  draggable="false"
                />
                {/* Anti-download overlay */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => e.preventDefault()}
                  aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 z-20">
                  <p className="text-white text-sm font-semibold">{rdImages[0].caption}</p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-6">
                <i className="ri-flask-line text-sm w-4 h-4 flex items-center justify-center" />
                Investigación y Desarrollo
              </div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
                Innovación Tecnológica <span className="text-secondary-500">de Vanguardia</span>
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 text-left sm:text-justify">
                Rixin Group invierte continuamente en investigación y desarrollo para mantenerse a la vanguardia
                de la tecnología química aplicada a la industria petrolera. Su equipo de I+D trabaja en nuevas
                formulaciones de demulsificantes con mayor eficiencia, menor dosis y compatibilidad con crudos
                de alta densidad como los de la Faja Petrolífera del Orinoco.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 text-center">
                  <div className="font-heading text-2xl font-bold text-primary-500 mb-1">200+</div>
                  <p className="text-foreground-500 text-xs">Investigadores</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 text-center">
                  <div className="font-heading text-2xl font-bold text-secondary-500 mb-1">60</div>
                  <p className="text-foreground-500 text-xs">Doctores y Magísteres</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 text-center">
                  <div className="font-heading text-2xl font-bold text-accent-500 mb-1">15</div>
                  <p className="text-foreground-500 text-xs">Doctores (PhD)</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4 border border-background-200/60 text-center">
                  <div className="font-heading text-2xl font-bold text-primary-500 mb-1">50</div>
                  <p className="text-foreground-500 text-xs">Magísteres (MSc)</p>
                </div>
              </div>
            </div>
          </div>

          {/* RD Areas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {rdAreas.map((area) => (
              <div
                key={area.title}
                className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/60"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className={`${area.icon} text-xl`} />
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-3">{area.title}</h3>
                <p className="text-foreground-500 text-sm leading-relaxed text-justify">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={rdImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={closeLightbox}
        onNext={closeLightbox}
      />
    </section>
  );
}